import React, { useState, useMemo } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ReferenceLine } from "recharts";
import {
  LayoutGrid, FileText, TrendingUp, Receipt, PhoneCall, Gauge, Scale, Banknote,
  Send, Sparkles, X, ShieldCheck, AlertTriangle, RefreshCw, Inbox, Activity,
  CheckCircle2, Droplets,
} from "lucide-react";

/* Radha — Module 8: Cash Flow Forecasting + Scenario Intelligence (preview)
   Seeded to match 008_cashflow.sql. stressed_net = base*(1-factor); band from runway. */

const C = {
  primary: "#1B54F8", success: "#00A87A", warning: "#C8861A", danger: "#D63B2F",
  ai: "#6E44D4", aiBg: "#F5F3FF", bg: "#F3F2EE", surface: "#FFFFFF",
  border: "#E4E3DE", text: "#08090C", muted: "#7A7975",
};
const mono = "'DM Mono', ui-monospace, Menlo, monospace";
const sans = "'Inter', system-ui, sans-serif";
const fmtCr = (n) => `₹${n.toFixed(1)}Cr`;

/* cashflow periods (₹ Cr) from 008_cashflow.sql */
const PERIODS = [
  { m: "Jan", net: 3.5, bal: 33.5 }, { m: "Feb", net: 1.0, bal: 34.5 },
  { m: "Mar", net: 4.0, bal: 38.5 }, { m: "Apr", net: 3.0, bal: 41.5 },
  { m: "May", net: 0.5, bal: 42.0 }, { m: "Jun", net: 2.0, bal: 44.0 },
  { m: "Jul", net: 1.0, bal: 45.0 }, { m: "Aug", net: 0.5, bal: 45.5 },
];

const BASE_NET = 2.0; // ₹2.0Cr monthly base net
const SCENARIOS = [
  { type: "optimistic", name: "Early collections", factor: -0.15, color: C.success },
  { type: "base", name: "Base plan", factor: 0.0, color: C.primary },
  { type: "stress", name: "DSO +15 days", factor: 0.35, color: C.warning },
  { type: "severe_stress", name: "Top-2 customer delay", factor: 0.60, color: C.danger },
].map(s => ({ ...s, stressed: +(BASE_NET * (1 - s.factor)).toFixed(2) }));

const DRIVERS = [
  { driver: "DSO rises from 52 to 67 days", impact: -0.7, prob: 0.45 },
  { driver: "Reliance + L&T delay ₹10Cr to next quarter", impact: -1.2, prob: 0.30 },
  { driver: "5 customers take 2% early-pay discount", impact: 0.3, prob: 0.55 },
];

const LIQUIDITY = { days: 195, available: 32.0, cash: 44.0, committed: 12.0 };

const MODULES = [
  { code: "M1", name: "Collections Copilot", icon: PhoneCall },
  { code: "M2", name: "Dispute Resolution", icon: Scale },
  { code: "M3", name: "Contract MIS", icon: FileText },
  { code: "M4", name: "Revenue Forecast", icon: TrendingUp },
  { code: "M5", name: "Invoicing Engine", icon: Receipt },
  { code: "M6", name: "Payment Reminders", icon: Send },
  { code: "M7", name: "Cash Application", icon: Banknote },
  { code: "M8", name: "Cash Flow Forecast", icon: Gauge, active: true },
  { code: "M9", name: "CRM Pipeline", icon: LayoutGrid },
];

function classifyBand(days) {
  if (days >= 180) return { band: "healthy", color: C.success };
  if (days >= 90) return { band: "watch", color: C.primary };
  if (days >= 30) return { band: "tight", color: C.warning };
  return { band: "critical", color: C.danger };
}
function riskFor(base, stressed, days) {
  const band = classifyBand(days).band;
  const drop = base > 0 ? (base - stressed) / base : 0;
  if (band === "critical" || stressed <= 0) return "high";
  if (band === "tight" || drop >= 0.5) return "high";
  if (band === "watch" || drop >= 0.3) return "medium";
  return "low";
}

function Badge({ children, color }) {
  return <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: 0.5, textTransform: "uppercase", color, background: `${color}14`, border: `0.5px solid ${color}33`, borderRadius: 20, padding: "3px 8px", whiteSpace: "nowrap" }}>{children}</span>;
}
function KPICard({ label, value, sub, accent }) {
  return <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "16px 18px", flex: 1, minWidth: 140 }}>
    <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>{label}</div>
    <div style={{ fontFamily: mono, fontSize: 24, fontWeight: 500, color: accent || C.text, marginTop: 8 }}>{value}</div>
    <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sub}</div>
  </div>;
}

export default function Cashflow() {
  const [view, setView] = useState("data");
  const [drawer, setDrawer] = useState(false);
  const band = classifyBand(LIQUIDITY.days);

  return (
    <div style={{ display: "flex", minHeight: 640, background: C.bg, fontFamily: sans, color: C.text }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        .nav:hover{background:#ffffff14}`}</style>

      <aside style={{ width: 224, background: "#0E1118", color: "#E7E9EE", padding: "20px 12px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 8px 18px" }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: C.ai, display: "grid", placeItems: "center", fontFamily: mono, fontWeight: 600 }}>र</div>
          <div style={{ fontWeight: 600 }}>Radha</div>
        </div>
        {MODULES.map(m => { const Icon = m.icon; return (
          <div key={m.code} className="nav" style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8, marginBottom: 2, cursor: "pointer", background: m.active ? C.primary : "transparent", color: m.active ? "#fff" : "#AEB4C0" }}>
            <Icon size={15} /><span style={{ fontSize: 12.5 }}>{m.name}</span>
            <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 8, opacity: 0.6 }}>{m.code}</span>
          </div>); })}
        <div style={{ marginTop: 18, padding: "0 8px", fontSize: 10, color: "#5A6172" }}>Bharat Industries Ltd</div>
      </aside>

      <main style={{ flex: 1, padding: "22px 26px", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontFamily: mono, fontSize: 10, color: C.muted, letterSpacing: 0.6 }}>M8 · CASH FLOW FORECASTING + SCENARIO INTELLIGENCE</div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: "4px 0 0" }}>Cash Flow & Scenarios</h1>
          </div>
          <div style={{ display: "flex", gap: 4, background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: 4 }}>
            {["data", "loading", "empty", "error"].map(s => (
              <button key={s} onClick={() => setView(s)} style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5, border: "none", borderRadius: 7, padding: "5px 9px", cursor: "pointer", background: view === s ? C.text : "transparent", color: view === s ? "#fff" : C.muted }}>{s}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
          <KPICard label="Closing Balance" value={fmtCr(LIQUIDITY.cash)} sub="latest month-end" accent={C.primary} />
          <KPICard label="Base Monthly Net" value={fmtCr(BASE_NET)} sub="bottom-up base case" accent={C.success} />
          <KPICard label="Runway" value={`${LIQUIDITY.days}d`} sub={band.band} accent={band.color} />
          <KPICard label="Scenarios" value={SCENARIOS.length} sub="modelled" />
        </div>

        <div style={{ display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 540px", minWidth: 320 }}>
            {/* EF-CORE liquidity-risk panel */}
            <div style={{ background: C.aiBg, border: `0.5px solid ${C.ai}33`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Sparkles size={14} color={C.ai} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.8, color: C.ai }}>EF-CORE · LIQUIDITY RISK</span>
                <span style={{ marginLeft: "auto" }}><Badge color={C.danger}>high risk</Badge></span>
              </div>
              <p style={{ margin: "10px 0 0", fontSize: 13.5, lineHeight: 1.5, color: "#2A2A2E" }}>
                Under the <b>severe-stress</b> scenario (top-2 customers delay a quarter), monthly net cash falls ~60% to
                <b> ₹0.8Cr</b> and runway compresses toward the tight band. Pre-arrange a working-capital line and accelerate
                collections on the two largest accounts before committing Q3 outflows.
              </p>
              <button onClick={() => setDrawer(true)} style={{ marginTop: 12, background: C.ai, color: "#fff", border: "none", borderRadius: 8, padding: "7px 13px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>Open stress tester</button>
            </div>

            {/* Balance + net chart */}
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: "14px 14px 8px", marginBottom: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted, marginBottom: 8 }}>Net cash flow & closing balance (₹ Cr)</div>
              {view === "loading" ? <Skeleton h={220} /> : view === "error" ? <ErrorState /> : view === "empty" ? <EmptyState /> : (
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={PERIODS} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="2 4" stroke={C.border} vertical={false} />
                    <XAxis dataKey="m" tick={{ fontSize: 10, fill: C.muted, fontFamily: mono }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="l" tick={{ fontSize: 10, fill: C.muted, fontFamily: mono }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: C.muted, fontFamily: mono }} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v) => `₹${v}Cr`} contentStyle={{ borderRadius: 8, border: `0.5px solid ${C.border}`, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: mono }} />
                    <Bar yAxisId="l" dataKey="net" name="Net cash" fill={C.primary} radius={[3, 3, 0, 0]} barSize={18} />
                    <Line yAxisId="r" type="monotone" dataKey="bal" name="Closing balance" stroke={C.success} strokeWidth={2.5} dot={{ r: 2 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Scenario comparison */}
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
              <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted, marginBottom: 4 }}>Scenario comparison · monthly net</div>
              {SCENARIOS.map((sc, i) => {
                const pct = (sc.stressed / 2.5) * 100;
                return (
                  <div key={i} style={{ marginTop: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12.5 }}>{sc.name} <span style={{ color: C.muted, fontFamily: mono, fontSize: 10 }}>({sc.factor > 0 ? "+" : ""}{Math.round(sc.factor * 100)}% stress)</span></span>
                      <span style={{ fontFamily: mono, fontSize: 13, color: sc.color }}>{fmtCr(sc.stressed)}</span>
                    </div>
                    <div style={{ height: 8, background: C.bg, borderRadius: 4, marginTop: 5, overflow: "hidden" }}>
                      <div style={{ width: `${Math.max(2, pct)}%`, height: "100%", background: sc.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Liquidity gauge + drivers */}
          <div style={{ flex: "0 0 250px", minWidth: 210 }}>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
                <Droplets size={13} color={band.color} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Liquidity band</span>
              </div>
              <div style={{ fontFamily: mono, fontSize: 26, fontWeight: 500, color: band.color }}>{LIQUIDITY.days}d</div>
              <div style={{ marginTop: 8 }}><Badge color={band.color}>{band.band}</Badge></div>
              {/* band scale */}
              <div style={{ display: "flex", height: 6, borderRadius: 4, overflow: "hidden", marginTop: 12 }}>
                {[["critical", C.danger, 30], ["tight", C.warning, 60], ["watch", C.primary, 90], ["healthy", C.success, 100]].map((b, i) => (
                  <div key={i} style={{ flex: b[2], background: b[1], opacity: band.band === b[0] ? 1 : 0.3 }} title={b[0]} />
                ))}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>{fmtCr(LIQUIDITY.available)} available · {fmtCr(LIQUIDITY.committed)} committed</div>
            </div>
            <div style={{ background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                <Activity size={13} color={C.muted} />
                <span style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Scenario drivers</span>
              </div>
              {DRIVERS.map((d, i) => (
                <div key={i} style={{ marginTop: 12, paddingTop: i ? 12 : 0, borderTop: i ? `0.5px solid ${C.border}` : "none" }}>
                  <div style={{ fontSize: 12.5, lineHeight: 1.35 }}>{d.driver}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                    <span style={{ fontFamily: mono, fontSize: 11, color: d.impact >= 0 ? C.success : C.danger }}>{d.impact >= 0 ? "+" : ""}{fmtCr(d.impact)}</span>
                    <span style={{ fontFamily: mono, fontSize: 10, color: C.muted }}>{Math.round(d.prob * 100)}% likely</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {drawer && <StressDrawer onClose={() => setDrawer(false)} />}
    </div>
  );
}

function Skeleton({ h = 200 }) { return <div style={{ padding: 14 }}>{[...Array(4)].map((_, i) => <div key={i} style={{ height: 16, background: C.border, borderRadius: 6, marginBottom: 14, opacity: 0.6, animation: "pulse 1.4s ease-in-out infinite" }} />)}<style>{`@keyframes pulse{0%,100%{opacity:.35}50%{opacity:.7}}`}</style></div>; }
function ErrorState() { return <div style={{ padding: 40, textAlign: "center" }}><AlertTriangle size={26} color={C.danger} /><div style={{ marginTop: 10, fontWeight: 500 }}>Couldn’t load cash flow</div><button style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6, background: C.primary, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}><RefreshCw size={13} /> Retry</button></div>; }
function EmptyState() { return <div style={{ padding: 44, textAlign: "center" }}><Inbox size={26} color={C.muted} /><div style={{ marginTop: 10, fontWeight: 500 }}>No cash-flow data</div><div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Import bank balances and projections to begin.</div></div>; }

function StressDrawer({ onClose }) {
  const [factor, setFactor] = useState(0.6);
  const [runway, setRunway] = useState(195);
  const stressed = +(BASE_NET * (1 - factor)).toFixed(2);
  const band = classifyBand(runway);
  const risk = riskFor(BASE_NET, stressed, runway);
  const riskColor = { high: C.danger, medium: C.warning, low: C.success }[risk];
  const drop = Math.round((1 - stressed / BASE_NET) * 100);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "#08090C55", display: "flex", justifyContent: "flex-end", zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 400, background: C.surface, height: "100%", padding: 22, overflow: "auto", boxShadow: "-12px 0 40px #0003" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: mono, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Scenario stress tester</div>
          <X size={18} color={C.muted} style={{ cursor: "pointer" }} onClick={onClose} />
        </div>

        <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
          <div style={{ flex: 1, background: C.bg, borderRadius: 10, padding: 12, textAlign: "center" }}>
            <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", color: C.muted }}>Base net</div>
            <div style={{ fontFamily: mono, fontSize: 18, marginTop: 4 }}>{fmtCr(BASE_NET)}</div>
          </div>
          <div style={{ flex: 1, background: C.bg, borderRadius: 10, padding: 12, textAlign: "center" }}>
            <div style={{ fontFamily: mono, fontSize: 9, textTransform: "uppercase", color: C.muted }}>Stressed net</div>
            <div style={{ fontFamily: mono, fontSize: 18, marginTop: 4, color: riskColor }}>{fmtCr(stressed)}</div>
          </div>
        </div>

        <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontFamily: mono, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Stress factor</span>
          <span style={{ fontFamily: mono, fontSize: 12, color: riskColor }}>{factor > 0 ? "+" : ""}{Math.round(factor * 100)}% · −{drop}% net</span>
        </div>
        <input type="range" min="-0.3" max="0.8" step="0.05" value={factor} onChange={e => setFactor(+e.target.value)}
          style={{ width: "100%", marginTop: 8, accentColor: C.ai }} />
        <div style={{ display: "flex", justifyContent: "space-between", fontFamily: mono, fontSize: 9, color: C.muted }}>
          <span>upside −30%</span><span>downside +80%</span>
        </div>

        <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontFamily: mono, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: C.muted }}>Assumed runway</span>
          <span style={{ fontFamily: mono, fontSize: 12, color: band.color }}>{runway} days · {band.band}</span>
        </div>
        <input type="range" min="10" max="240" step="5" value={runway} onChange={e => setRunway(+e.target.value)}
          style={{ width: "100%", marginTop: 8, accentColor: band.color }} />

        {/* live verdict */}
        <div style={{ marginTop: 18, background: `${riskColor}10`, border: `0.5px solid ${riskColor}33`, borderRadius: 10, padding: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {risk === "low" ? <CheckCircle2 size={15} color={riskColor} /> : <AlertTriangle size={15} color={riskColor} />}
            <span style={{ fontFamily: mono, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: riskColor }}>{risk} risk</span>
          </div>
          <p style={{ margin: "8px 0 0", fontSize: 12.5, lineHeight: 1.5, color: "#2A2A2E" }}>
            Scenario compresses monthly net by {drop}% to {fmtCr(stressed)}; projected liquidity band is
            <b> {band.band}</b> ({runway} days runway).
          </p>
        </div>

        <div style={{ marginTop: 12, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
          Risk level and band are computed deterministically — the same rules the API and DB enforce. Generating a
          proposal records the assumptions to the immutable board audit trail.
        </div>
      </div>
    </div>
  );
}
